import { create } from 'zustand';
import { sandboxApi } from '../services/api';

export const useSandboxStore = create((set, get) => ({
  // State
  sandboxes: [],
  activeSandboxes: [],
  templates: [],
  selectedSandbox: null,
  sandboxInstances: [],
  loading: false,
  error: null,

  // Actions
  fetchSandboxes: async (token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.getUserSandboxes(token);
      // Get current user ID from auth store to determine ownership
      const getUserId = () => {
        try {
          const authStore = JSON.parse(localStorage.getItem('auth-storage') || '{}');
          return authStore?.state?.userInfo?.id;
        } catch {
          return null;
        }
      };
      const currentUserId = getUserId();

      const sandboxesWithType = (response || []).map(sandbox => ({
        ...sandbox,
        type: 'sandbox',
        isOwned: currentUserId ? sandbox.owner === currentUserId : false
      }));
      set({
        sandboxes: sandboxesWithType,
        loading: false,
      });
      return sandboxesWithType;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchActiveSandboxes: async (token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.getActiveSandboxes(token);
      set({
        activeSandboxes: response || [],
        loading: false,
      });
      return response;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchTemplates: async (token) => {
    set({ loading: true, error: null });
    try {
      const templates = await sandboxApi.getTemplates(token);
      // Get current user ID from auth store to determine ownership
      const getUserId = () => {
        try {
          const authStore = JSON.parse(localStorage.getItem('auth-storage') || '{}');
          return authStore?.state?.userInfo?.id;
        } catch {
          return null;
        }
      };
      const currentUserId = getUserId();

      const templatesWithMeta = (templates || []).map(template => ({
        ...template,
        type: 'template',
        isOwned: currentUserId ? template.owner === currentUserId : false
      }));
      set({
        templates: templatesWithMeta,
        loading: false,
      });
      return templatesWithMeta;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchSandboxById: async (sandboxId, token) => {
    set({ loading: true, error: null });
    try {
      const sandbox = await sandboxApi.getSandbox(sandboxId, token);
      set({
        selectedSandbox: sandbox,
        loading: false,
      });
      return sandbox;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createSandbox: async (sandboxData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.createSandbox(sandboxData, token);

      // Update sandboxes list
      const sandboxesResponse = await sandboxApi.getUserSandboxes(token);
      set({
        sandboxes: sandboxesResponse,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createSandboxFromTemplate: async (templateId, sandboxData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.createSandboxFromTemplate(
        templateId,
        sandboxData,
        token
      );

      // Update sandboxes list
      const sandboxesResponse = await sandboxApi.getUserSandboxes(token);
      set({
        sandboxes: sandboxesResponse,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateSandbox: async (sandboxId, sandboxData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.updateSandbox(
        sandboxId,
        sandboxData,
        token
      );

      // Update sandbox in lists
      set((state) => ({
        sandboxes: state.sandboxes.map((s) =>
          s.id === sandboxId ? response : s
        ),
        selectedSandbox:
          state.selectedSandbox?.id === sandboxId
            ? response
            : state.selectedSandbox,
        loading: false,
      }));

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteSandbox: async (sandboxId, token) => {
    set({ loading: true, error: null });
    try {
      await sandboxApi.deleteSandbox(sandboxId, token);

      // Remove sandbox from lists
      set((state) => ({
        sandboxes: state.sandboxes.filter((s) => s.id !== sandboxId),
        selectedSandbox:
          state.selectedSandbox?.id === sandboxId
            ? null
            : state.selectedSandbox,
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  startSandbox: async (sandboxId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.startSandbox(sandboxId, token);

      // Update sandbox status
      set((state) => ({
        sandboxes: state.sandboxes.map((s) =>
          s.id === sandboxId ? { ...s, status: 'running' } : s
        ),
        selectedSandbox:
          state.selectedSandbox?.id === sandboxId
            ? { ...state.selectedSandbox, status: 'running' }
            : state.selectedSandbox,
        loading: false,
      }));

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  stopSandbox: async (sandboxId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.stopSandbox(sandboxId, token);

      // Update sandbox status
      set((state) => ({
        sandboxes: state.sandboxes.map((s) =>
          s.id === sandboxId ? { ...s, status: 'stopped' } : s
        ),
        selectedSandbox:
          state.selectedSandbox?.id === sandboxId
            ? { ...state.selectedSandbox, status: 'stopped' }
            : state.selectedSandbox,
        loading: false,
      }));

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  shareSandbox: async (sandboxId, shareData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.shareSandbox(
        sandboxId,
        shareData,
        token
      );
      set({ loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createTemplate: async (sandboxId, templateData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.createTemplate(
        sandboxId,
        templateData,
        token
      );

      // Update templates list
      const templatesResponse = await sandboxApi.getTemplates(token);
      set({
        templates: templatesResponse,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteTemplate: async (templateId, token) => {
    set({ loading: true, error: null });
    try {
      await sandboxApi.deleteTemplate(templateId, token);

      // Remove template from list
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== templateId),
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  shareTemplate: async (templateId, shareData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await sandboxApi.shareTemplate(
        templateId,
        shareData,
        token
      );
      set({ loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Sandbox instances
  fetchSandboxInstances: async (token) => {
    try {
      const response = await sandboxApi.getSandboxInstances(token);
      set({ sandboxInstances: response });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // File operations
  getSandboxFiles: async (sandboxId, path, token) => {
    try {
      const response = await sandboxApi.getSandboxFiles(sandboxId, path, token);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getSandboxFileContent: async (sandboxId, filePath, token) => {
    try {
      const response = await sandboxApi.getSandboxFileContent(
        sandboxId,
        filePath,
        token
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateSandboxFile: async (sandboxId, filePath, content, token) => {
    try {
      const response = await sandboxApi.updateSandboxFile(
        sandboxId,
        filePath,
        content,
        token
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  createSandboxFile: async (sandboxId, filePath, content, token) => {
    try {
      const response = await sandboxApi.createSandboxFile(
        sandboxId,
        filePath,
        content,
        token
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  deleteSandboxFile: async (sandboxId, filePath, token) => {
    try {
      const response = await sandboxApi.deleteSandboxFile(
        sandboxId,
        filePath,
        token
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Terminal operations
  executeCommand: async (sandboxId, command, token) => {
    try {
      const response = await sandboxApi.executeCommand(
        sandboxId,
        command,
        token
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  selectSandbox: (sandboxId) => {
    const sandbox = get().sandboxes.find((s) => s.id === sandboxId);
    set({ selectedSandbox: sandbox });
  },

  clearSelectedSandbox: () => set({ selectedSandbox: null }),

  clearError: () => set({ error: null }),
}));
